import React, { useEffect, useState, useMemo } from 'react';
import { Box, Paper, Grid, Typography, Alert, Divider, CircularProgress } from '@mui/material';
import DynamicFormRenderer from '../ProbationReview/DynamicFormRenderer';
import { getAnnualReview, getAllReviews } from './AnnualReviewQueries';

const unwrap = (v) => (v && typeof v === 'object' && 'S' in v ? v.S : v);
const safeJSON = (v) => {
  const raw = unwrap(v);
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};
const getEmailFromLocalStorage = () => {
  const raw = localStorage.getItem('user');
  if (!raw) return '';
  try {
    const obj = JSON.parse(raw);
    return unwrap(obj.email || (obj.user && obj.user.email) || obj.user_email || obj.employee_email) || '';
  } catch { return raw.includes('@') ? raw : ''; }
};

const ManagerCommentsView = ({ reviewsCache = [], formSchema, periodStart }) => {
  const [managerReview, setManagerReview] = useState(null);
  const [hasSelfReview, setHasSelfReview] = useState(false);
  const [loading, setLoading] = useState(true);

  const userEmail = useMemo(() => String(getEmailFromLocalStorage() || '').toLowerCase(), []);

  useEffect(() => {
    if (!userEmail) return;

    const loadReview = async () => {
      setLoading(true);
      try {
        let match = null;

        // Try direct fetch by id first (most reliable)
        if (periodStart) {
          const id = `${userEmail}_annual_${periodStart}`;
          const directResult = await getAnnualReview(userEmail, id);
          const item = directResult?.review || directResult?.Item || directResult;
          if (item && (unwrap(item?.email) || unwrap(item?.id))) {
            match = item;
          }
        }

        // Fallback: search in reviewsCache
        if (!match && Array.isArray(reviewsCache) && reviewsCache.length) {
          match = reviewsCache.find((it) => {
            const v = unwrap(it?.email || it?.employee_email);
            const emailMatch = String(v || '').toLowerCase() === userEmail;
            if (periodStart) {
              return emailMatch && unwrap(it?.period_start) === periodStart;
            }
            return emailMatch;
          });
        }

        // Last resort: fetch all and search
        if (!match) {
          const allRes = await getAllReviews();
          const allItems = Array.isArray(allRes?.reviews) ? allRes.reviews
            : Array.isArray(allRes?.Items) ? allRes.Items
            : Array.isArray(allRes) ? allRes : [];
          match = allItems.find((it) => {
            const v = unwrap(it?.email || it?.employee_email);
            const emailMatch = String(v || '').toLowerCase() === userEmail;
            if (periodStart) {
              return emailMatch && unwrap(it?.period_start) === periodStart;
            }
            return emailMatch;
          });
        }

        if (match) {
          const userResp = safeJSON(match.user_response) || match.user_response || null;
          setHasSelfReview(userResp && typeof userResp === 'object' && Object.keys(userResp).length > 0);
          const managerResp = safeJSON(match.manager_response) || match.manager_response || null;
          setManagerReview(managerResp?.responses || managerResp);
        } else {
          setManagerReview(null);
          setHasSelfReview(false);
        }
      } catch (e) {
        console.error('[ManagerCommentsView] Error loading review:', e);
        setManagerReview(null);
      } finally {
        setLoading(false);
      }
    };

    loadReview();
  }, [userEmail, periodStart]);

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Manager Comments</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>To be completed by the manager after reviewing the employee's self-evaluation</Typography>
      <Divider sx={{ mb: 2 }} />
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
      ) : managerReview && typeof managerReview === 'object' && Object.keys(managerReview).length > 0 ? (
        formSchema ? (
          <DynamicFormRenderer
            formSchema={formSchema}
            formData={managerReview}
            setFormData={() => {}}
            sectionFilter="manager"
            readOnly={true}
          />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}><Typography variant="body2"><strong>Accomplishments:</strong> {managerReview.accomplishments || '-'}</Typography></Grid>
            <Grid item xs={12}><Typography variant="body2"><strong>Development Areas:</strong> {managerReview.developmentAreas || '-'}</Typography></Grid>
            <Grid item xs={12} md={3}><Typography variant="body2"><strong>Dependability:</strong> {managerReview.dependability || '-'}</Typography></Grid>
            <Grid item xs={12} md={3}><Typography variant="body2"><strong>Teamwork:</strong> {managerReview.teamwork || '-'}</Typography></Grid>
            <Grid item xs={12} md={3}><Typography variant="body2"><strong>Productivity:</strong> {managerReview.productivity || '-'}</Typography></Grid>
            <Grid item xs={12} md={3}><Typography variant="body2"><strong>Organizational Commitment:</strong> {managerReview.organizationalCommitment || '-'}</Typography></Grid>
            <Grid item xs={12} md={3}><Typography variant="body2"><strong>Overall Performance:</strong> {managerReview.overallPerformance || '-'}</Typography></Grid>
            <Grid item xs={12}><Typography variant="body2"><strong>Summary:</strong> {managerReview.summary || '-'}</Typography></Grid>
          </Grid>
        )
      ) : !hasSelfReview ? (
        <Alert severity="warning">You need to complete your self-review before manager comments can be submitted.</Alert>
      ) : (
        <Alert severity="info">Your manager has not submitted comments yet. They will appear here once submitted.</Alert>
      )}
    </Paper>
  );
};

export default ManagerCommentsView;
